import { Fragment, useMemo } from 'react';
import type {
  DivergenceChapter,
  DivergenceVerse,
  KuralGraphEntry,
} from '../data/types';
import { useKural } from '../hooks/useKural';
import KeepLink from './KeepLink';
import './DivergenceReadingRoom.css';

interface Props {
  kural: KuralGraphEntry;
  divergence: DivergenceVerse;
  chapter: DivergenceChapter;
  onClose: () => void;
}

const TIER_NAME: Record<number, string> = {
  0: 'Agreement',
  1: 'Emphasis',
  2: 'Gloss',
  3: 'Frame',
  4: 'Referent',
};

const TIER_HINT: Record<number, string> = {
  0: 'Both read the verse the same way; only elaboration differs.',
  1: 'Same subject, but the two pull toward opposed poles or significances.',
  2: 'A key word is glossed one way and another; both cannot be true.',
  3: 'One imports a named scheme the other\'s plain reading rejects.',
  4: 'The whole-verse subject or theological target differs.',
};

export default function DivergenceReadingRoom({
  kural,
  divergence,
  chapter,
  onClose,
}: Props) {
  const full = useKural(kural.n);
  const cruxRe = useMemo(() => escapeForRegex(divergence.crux_term), [
    divergence.crux_term,
  ]);
  const mFull = full.data?.paraphrases.find((p) => p.source === 'manakkudavar')?.text;
  const pFull = full.data?.paraphrases.find((p) => p.source === 'parimelazhagar')?.text;
  const mText = mFull || divergence.manakkudavar_reading;
  const pText = pFull || divergence.parimelazhagar_reading;
  const scorePct = Math.round(divergence.intensity * 100);
  const anyAxis =
    divergence.axes.gloss ||
    divergence.axes.referent ||
    divergence.axes.emphasis ||
    divergence.axes.frame;

  return (
    <article
      className={`dvroom dvroom--t${divergence.tier}`}
      aria-labelledby={`dvroom-kural-${kural.n}`}
    >
      <button
        type="button"
        className="dvroom__close"
        onClick={onClose}
        aria-label="Close reading room"
      >
        ×
      </button>

      <header className="dvroom__head">
        <div className="dvroom__lineage">
          <span>Kural {kural.n}</span>
          <span aria-hidden="true">·</span>
          <span>
            அதிகாரம் {chapter.n} · <span lang="ta">{chapter.title_ta}</span> · {chapter.title_en}
          </span>
        </div>
        <div className="dvroom__verse" lang="ta" id={`dvroom-kural-${kural.n}`}>
          {kural.verse_ta.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
        <div className="dvroom__gist">{kural.gist_en}</div>
      </header>

      <div className="dvroom__duel">
        <UraiColumn
          who="Manakkudavar"
          era="~10th c."
          side="m"
          text={mText}
          cruxRe={cruxRe}
          loading={full.loading && !mFull}
        />
        <UraiColumn
          who="Parimelazhagar"
          era="~13th c."
          side="p"
          text={pText}
          cruxRe={cruxRe}
          loading={full.loading && !pFull}
        />
      </div>

      <section className="dvroom__judge" aria-labelledby="dvroom-judge-h">
        <div className="dvroom__judge-head">
          <h4 className="sr-only" id="dvroom-judge-h">Assessment</h4>
          <span className={`dvroom__tier-badge dvroom__tier-badge--t${divergence.tier}`}>
            Tier {divergence.tier} · {TIER_NAME[divergence.tier]}
          </span>
          <span className="dvroom__meta">
            {formatAgreement(divergence.agreement)} · {divergence.confidence} confidence
          </span>
          <span className="dvroom__scorebar" aria-hidden="true">
            <span
              className="dvroom__scorebar-fill"
              style={{ transform: `scaleX(${Math.max(0.04, divergence.intensity)})` }}
            />
          </span>
          <span className="dvroom__score">{scorePct}</span>
        </div>

        {divergence.crux_term && (
          <div className="dvroom__crux">
            <span className="dvroom__crux-label">Crux term</span>
            <span className="dvroom__crux-term" lang="ta">{divergence.crux_term}</span>
          </div>
        )}

        {anyAxis && (
          <div className="dvroom__axes" role="group" aria-label="Axes of disagreement">
            <AxisChip label="Gloss" lit={divergence.axes.gloss} />
            <AxisChip label="Referent" lit={divergence.axes.referent} />
            <AxisChip label="Emphasis" lit={divergence.axes.emphasis} />
            <AxisChip label="Frame" lit={divergence.axes.frame} />
          </div>
        )}

        <p className="dvroom__hint">{TIER_HINT[divergence.tier]}</p>

        {divergence.reason && (
          <p className="dvroom__verdict">
            <strong>Why this matters. </strong>
            {divergence.reason}
          </p>
        )}
      </section>

      <footer className="dvroom__foot">
        <a
          href="#method"
          className="dvroom__method-link"
          onClick={(e) => {
            e.preventDefault();
            const el = document.getElementById('method');
            if (!el) return;
            const prefersReduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            el.scrollIntoView({ behavior: prefersReduce ? 'auto' : 'smooth', block: 'start' });
          }}
        >
          How is this scored? ↓
        </a>
        <KeepLink to={`/kural/${kural.n}`} className="dvroom__open-kural">
          Open the full kural page →
        </KeepLink>
      </footer>
    </article>
  );
}

function UraiColumn({
  who,
  era,
  side,
  text,
  cruxRe,
  loading,
}: {
  who: string;
  era: string;
  side: 'm' | 'p';
  text: string;
  cruxRe: string | null;
  loading: boolean;
}) {
  return (
    <div className={`dvroom__urai dvroom__urai--${side}`}>
      <div className="dvroom__urai-head">
        <span className="dvroom__urai-who">{who}</span>
        <span className="dvroom__urai-era">{era}</span>
        {loading && (
          <span className="dvroom__urai-loading" aria-live="polite">
            loading full urai…
          </span>
        )}
      </div>
      <blockquote className="dvroom__urai-body" lang="ta">
        {highlight(text, cruxRe)}
      </blockquote>
    </div>
  );
}

function AxisChip({ label, lit }: { label: string; lit: boolean }) {
  return (
    <span
      className={lit ? 'dvroom__axis dvroom__axis--lit' : 'dvroom__axis dvroom__axis--dim'}
      aria-label={lit ? `${label} axis fires` : `${label} axis does not fire`}
    >
      {label}
    </span>
  );
}

function escapeForRegex(term: string): string | null {
  if (!term) return null;
  const cleaned = term.replace(/[\s./\\|]+$/g, '').replace(/^\s+/g, '');
  if (!cleaned) return null;
  return cleaned.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlight(text: string, cruxRe: string | null): React.ReactNode {
  if (!cruxRe) return text;
  try {
    const re = new RegExp(`(${cruxRe})`, 'g');
    const parts = text.split(re);
    return parts.map((part, i) =>
      re.test(part) && i % 2 === 1 ? (
        <mark className="dvroom__urai-crux" key={i}>
          {part}
        </mark>
      ) : (
        <Fragment key={i}>{part}</Fragment>
      ),
    );
  } catch {
    return text;
  }
}

function formatAgreement(a: string): string {
  return a.charAt(0).toUpperCase() + a.slice(1);
}
